import { useContext } from "react";
import { useController } from "react-hook-form";
import { CREATOR_AND_UP, GUEST, type RoleName } from "common/authorization";
import type { PathArray } from "common/utils/formStructure";
import { AuthContext } from "../../../../../../../Authorization/AuthContext";
import { Title } from "../../../../../../../Common/Typography";
import { TagSelect } from "../../../../../../../Inputs/react-hook-form";
import {
  getUiSchemaPropertyFormPath,
  useBuilderFormContext,
} from "../../../form/form";
import { RoleAndGroupSelector } from "../../../RoleAndGroupSelector";
import { getInputOptionFromFieldPermissions } from "../../../utils";
import {
  VIEWER_TYPE,
  viewerTypeLabel,
  viewerTypeToFieldPermissionsMap,
} from "../../../viewerType";
import {
  FieldWrapper,
  FormSection,
  FormSectionTitleWrapper,
} from "../../__styles__/SidebarColumn";

interface FieldPermissionsConfigProps {
  fieldPath: PathArray;
  isReadOnly?: boolean;
}

export function FieldPermissionsConfig({
  fieldPath,
  isReadOnly,
}: FieldPermissionsConfigProps) {
  return (
    <FormSection>
      <FormSectionTitleWrapper>
        <Title size="small" type="semiBold">
          Default permissions
        </Title>
      </FormSectionTitleWrapper>
      <CreatorPermissionsConfig fieldPath={fieldPath} isReadOnly={isReadOnly} />
      <ViewerPermissionsConfig fieldPath={fieldPath} isReadOnly={isReadOnly} />
      <EditorPermissionsConfig fieldPath={fieldPath} isReadOnly={isReadOnly} />
    </FormSection>
  );
}

interface CreatorPermissionsConfigProps extends FieldPermissionsConfigProps {
  label?: string;
}

export function CreatorPermissionsConfig({
  fieldPath,
  label = "Who can fill out this field?",
  isReadOnly,
}: CreatorPermissionsConfigProps) {
  const { control } = useBuilderFormContext();
  const { account } = useContext(AuthContext);

  const userGroups = account?.userGroups ?? [];
  const creatorPermissionsPropertyFormPath = getUiSchemaPropertyFormPath(
    fieldPath,
    "viewOnCreate",
  );

  return (
    <FieldWrapper>
      <RoleAndGroupSelector
        control={control}
        name={creatorPermissionsPropertyFormPath}
        label={label}
        data-testid="field-creator-permissions"
        options={{ roles: [...CREATOR_AND_UP, ...GUEST], userGroups }}
        disabled={isReadOnly}
      />
    </FieldWrapper>
  );
}

export function ViewerPermissionsConfig({
  fieldPath,
  isReadOnly,
}: FieldPermissionsConfigProps) {
  const { account } = useContext(AuthContext);
  const { control } = useBuilderFormContext();

  const isPublicPortalEnabled = account?.publicPortal.enabled;

  const options =
    isPublicPortalEnabled ?
      [VIEWER_TYPE.TEAM, VIEWER_TYPE.TEAM_AND_GUESTS].map(value => ({
        value,
        label: viewerTypeLabel[value],
      }))
    : [VIEWER_TYPE.TEAM].map(value => ({
        value,
        label: viewerTypeLabel[value],
      }));

  const transform = {
    output: (value: Maybe<VIEWER_TYPE>) => {
      return value ? viewerTypeToFieldPermissionsMap[value] : [];
    },
    input: (value: Maybe<Array<RoleName>>) => {
      return getInputOptionFromFieldPermissions(
        value,
        viewerTypeToFieldPermissionsMap,
      );
    },
  };

  const viewerPermissionsPropertyFormPath = getUiSchemaPropertyFormPath(
    fieldPath,
    "viewOnRetrieve",
  );

  // This peculiar pattern is necessary because *this* component is not
  // inherently subscribed to the form state—the TagSelect component is (via the
  // `control`). Therefore, using `getFieldState` only provides the latest state
  // as of the last render…it does not cause a re-render when the form state
  // changes. Adding a `useController` call here subscribes this component to
  // the the field state and keeps the error message up to date.
  const { fieldState } = useController({
    name: viewerPermissionsPropertyFormPath,
  });
  const errorMessage = fieldState.error?.message;

  return (
    <FieldWrapper>
      <TagSelect
        control={control}
        name={viewerPermissionsPropertyFormPath}
        options={options}
        label="Who can view this field after the form is submitted?"
        iconName="eye"
        isMulti={false}
        disabled={!isPublicPortalEnabled || isReadOnly}
        transform={transform}
        required
        error={errorMessage}
        data-testid="field-viewer-permissions"
      />
    </FieldWrapper>
  );
}

export function EditorPermissionsConfig({
  fieldPath,
  isReadOnly,
}: FieldPermissionsConfigProps) {
  const { control } = useBuilderFormContext();
  const { account } = useContext(AuthContext);
  const userGroups = account?.userGroups ?? [];

  const editorPermissionsPropertyFormPath = getUiSchemaPropertyFormPath(
    fieldPath,
    "canUpdate",
  );

  // exclude "guest" from options
  const options = {
    roles: [...CREATOR_AND_UP, "publicUser" as const],
    userGroups,
  };

  return (
    <FieldWrapper>
      <RoleAndGroupSelector
        control={control}
        name={editorPermissionsPropertyFormPath}
        options={options}
        label="Who can edit this field after the form is submitted?"
        iconName="pencil"
        data-testid="field-editor-permissions"
        disabled={isReadOnly}
      />
    </FieldWrapper>
  );
}
